/**
 * Master Data — landing page.
 *
 * One tile per master-data entity, each linking to its dedicated manage
 * page. The tile shows a count from the relevant mock module so admins
 * see at a glance what's populated.
 */

import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAppSettings } from "../../../lib/appSettings";
import {
  BookOpenCheck,
  Building2,
  ChevronRight,
  Coins,
  FolderTree,
  Map as MapIcon,
  Percent,
  Ruler,
  Tags,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Button, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockBrands,
  mockCategories,
  type MockBrand,
  type MockCategory,
} from "../../../lib/catalogMock";
import {
  mockOpeningBalanceConfig,
  mockOpeningEntries,
  type MockOpeningBalanceConfig,
  type MockOpeningEntryRow,
} from "../../../lib/glMock";

export const Route = createFileRoute("/_app/settings/master-data")({
  component: MasterDataPage,
});

type Tile = {
  title: string;
  description: string;
  icon: LucideIcon;
  to: string;
  count?: number;
  countLabel?: string;
  comingSoon?: boolean;
};

function MasterDataPage() {
  const { branches, dealerAreas, taxRates, units } = useAppSettings();
  const [categories] = usePersistentMockState<MockCategory[]>("categories", mockCategories);
  const [brands] = usePersistentMockState<MockBrand[]>("brands", mockBrands);
  const [openingEntries] = usePersistentMockState<MockOpeningEntryRow[]>("openingEntries", mockOpeningEntries);
  const [openingBalanceConfig] = usePersistentMockState<MockOpeningBalanceConfig>("openingBalanceConfig", mockOpeningBalanceConfig);

  const counts = useMemo(() => {
    const liveBranches = branches.filter((b) => b.deletedAt === null && b.isActive).length;
    const liveAreas = dealerAreas.filter((a) => a.deletedAt === null).length;
    return {
      branches: liveBranches,
      areas: liveAreas,
      categories: categories.length,
      brands: brands.length,
      taxRates: taxRates.filter((r) => r.deletedAt === null).length,
      units: units.filter((u) => !u.isArchived).length,
      openingEntries: openingEntries.length,
      openingStatus: openingBalanceConfig.status,
    };
  }, [branches, dealerAreas, taxRates, units, categories, brands, openingEntries, openingBalanceConfig]);

  const tiles: Tile[] = [
    {
      title: "Branches & Factories",
      description:
        "HQ, factories and sales offices. Codes appear on every stock and sales record.",
      icon: Building2,
      to: "/settings/branches",
      count: counts.branches,
      countLabel: "active",
    },
    {
      title: "Dealer Areas",
      description:
        "Sales territories. Group dealers under a managing branch.",
      icon: MapIcon,
      to: "/settings/dealer-areas",
      count: counts.areas,
      countLabel: "areas",
    },
    {
      title: "Categories",
      description:
        "Product categorisation tree. Used for reporting and pricing.",
      icon: FolderTree,
      to: "/settings/categories",
      count: counts.categories,
      countLabel: "categories",
    },
    {
      title: "Brands",
      description: "Brand catalog tagged on each product.",
      icon: Tags,
      to: "/settings/brands",
      count: counts.brands,
      countLabel: "brands",
    },
    {
      title: "Tax Rates",
      description:
        "VAT, duty, withholding rates referenced by invoices.",
      icon: Percent,
      to: "/settings/tax-rates",
      count: counts.taxRates,
      countLabel: "rates",
    },
    {
      title: "Opening Balances",
      description:
        "Seed your day-one position — dealers, suppliers, loans, cash — before going live. Lock once balanced.",
      icon: BookOpenCheck,
      to: "/settings/opening-balances",
      count: counts.openingEntries,
      countLabel:
        counts.openingStatus === "closed"
          ? "closed"
          : counts.openingStatus === "posted"
            ? "posted"
            : "draft entries",
    },
    {
      title: "Units of Measure",
      description: "Pcs, kg, ctn — quantity units used everywhere.",
      icon: Ruler,
      to: "/settings/units",
      count: counts.units,
      countLabel: "units",
    },
    {
      title: "Currencies",
      description:
        "Foreign currencies accepted on POs and LCs. BDT is base.",
      icon: Coins,
      to: "/settings/master-data",
      countLabel: "BDT base",
      comingSoon: true,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Master Data"
        description="Reference entities that everything else points at — keep them clean and the rest of BizOP stays clean."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {tiles.map((tile) => (
          <Tile key={tile.title} tile={tile} />
        ))}
      </div>
    </div>
  );
}

function Tile({ tile }: { tile: Tile }) {
  const Icon = tile.icon;
  return (
    <div
      className={cn(
        "rounded-md border border-(--border-subtle) bg-(--bg-surface) p-4",
        "flex flex-col gap-3",
        "transition-colors hover:border-(--border-default)",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-md bg-(--bg-canvas) text-(--text-secondary)">
            <Icon className="size-4" />
          </div>
          <div>
            <div className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
              {tile.title}
            </div>
            {tile.count !== undefined ? (
              <div className="text-[12px] text-(--text-tertiary)">
                <span className="tabular-nums">{tile.count}</span>
                {tile.countLabel ? <span> {tile.countLabel}</span> : null}
              </div>
            ) : tile.countLabel ? (
              <div className="text-[12px] text-(--text-tertiary)">
                {tile.countLabel}
              </div>
            ) : null}
          </div>
        </div>
        {tile.comingSoon ? (
          <span className="rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-2 py-0.5 text-[10px] uppercase tracking-wide text-(--text-tertiary)">
            Soon
          </span>
        ) : null}
      </div>
      <p className="text-[12px] text-(--text-secondary)">
        {tile.description}
      </p>
      <div className="flex justify-end pt-1">
        <Button variant="ghost" size="sm" asChild>
          <Link to={tile.to as string}>
            Manage <ChevronRight className="size-3.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
